/*
	bf_gameautopick.ts
  Copyright (c) 2014-present Human Cube Inc.
*/

import BF from './bfconst1';
import { Game } from './gameTypes';


function calcZoneValues (game: Game, startingForces: number[][]) {
  // Returns an array of worth values for the zones, regardless of whether
  // or not they are currently owned or could be picked.
  const value = [-1];
  for(let i = 1; i <= game.zones; i++) {
    if( game.zoneFlags[i] & BF.ZONE_FLAG_LAND_ZONE ) {
      value[i] = 1;
      value[i] += game.zoneIncome[i] * 10;
      if( game.bordersLand[i].length === 0 ) {
        value[i] += 5;  // Island bonus.
      }
      for(let j = 0; j < game.forceID.length; j++) {
        if(startingForces[i][j] !== undefined) {
          value[i] += startingForces[i][j] * game.forceCost[j];
        }
      }
    }
    else value[i] = -1;  // Not for picking.
  }
  return value;
}

export function doScatteredAutoPick (game: Game, startingForces: number[][]) {
  const value = calcZoneValues(game, startingForces);

  const playerValue = Array<number>(game.players + 1).fill(0);
  for(let i = 1; i <= game.zones; i++) {
    if( game.zoneOwner[i] > 0 && game.zoneOwner[i] <= game.players ) {
      playerValue[game.zoneOwner[i]] += value[i];
    }
  }

  let bestValue;
  let bestIndex;
  do {
    // Assign next pick to player with lowest total worth:
    let playerIndex = 1;
    for(let p = 2; p <= game.players; p++) {
      if( playerValue[playerIndex] > playerValue[p] ) {
        playerIndex = p;
      }
    }

    bestValue = 0;
    bestIndex = 0;
    for(let i = 1; i <= game.zones; i++) {
      if( game.zoneOwner[i] === 0 && value[i] >= bestValue ) {
        bestValue = value[i];
        bestIndex = i;
      }
    }
    if( bestIndex > 0 ) {
      game.zoneOwner[bestIndex] = playerIndex;
      game.zoneOwnerPrev[bestIndex] = playerIndex;
      playerValue[playerIndex] += value[bestIndex];
    }
  } while( bestIndex > 0 );
}

export function doDistributedAutoPick (game: Game, startingForces: number[][]) {
  const value = calcZoneValues(game, startingForces);

  const playerValue = Array<number>(game.players + 1).fill(0);
  for(let i = 1; i <= game.zones; i++) {
    if( game.zoneOwner[i] > 0 && game.zoneOwner[i] <= game.players ) {
      playerValue[game.zoneOwner[i]] += value[i];
    }
  }

  // Find a random starting land zone for each if they do not already have one:
  for(let p = 1; p <= game.players; p++) {
    if( playerValue[p] === 0 ) {
      const zoneOffset = Math.floor(Math.random() * game.zones) + 1;
      for(let i = 1; i <= game.zones; i++) {
        const index = ((zoneOffset + i) % game.zones) + 1;
        if( game.zoneOwner[index] === 0 && value[index] >= 0 ) {
          game.zoneOwner[index] = p;
          game.zoneOwnerPrev[index] = p;
          playerValue[p] += value[index];
          break;
        }
      }
    }
  }

  let bestValue;
  let bestIndex;
  do {
    // Assign next pick to player with lowest total worth:
    let playerIndex = 1;
    for(let p = 2; p <= game.players; p++) {
      if( playerValue[playerIndex] > playerValue[p] ) {
        playerIndex = p;
      }
    }

    bestValue = 0;
    bestIndex = 0;
    for(let i = 1; i <= game.zones; i++) {
      if( game.zoneOwner[i] === 0 && value[i] >= bestValue ) {
        for(let j = 0; j < game.bordersLand[i].length; j++) {
          if( game.zoneOwner[game.bordersLand[i][j]] === playerIndex ) {
            // Must be a friendly neighbour.
            bestValue = value[i];
            bestIndex = i;
            break;
          }
        }
      }
    }
    if( bestIndex === 0 ) {
      for(let i = 1; i <= game.zones; i++) {
        if( game.zoneOwner[i] === 0 && value[i] >= bestValue ) {
          bestValue = value[i];
          bestIndex = i;
        }
      }
    }
    if( bestIndex > 0 ) {
      game.zoneOwner[bestIndex] = playerIndex;
      game.zoneOwnerPrev[bestIndex] = playerIndex;
      playerValue[playerIndex] += value[bestIndex];
    }
  } while( bestIndex > 0 );
}

export function do123123123AutoPick (game: Game, startingForces: number[][]) {
  const value = calcZoneValues(game, startingForces);
  let p = 1;
  let bestValue;
  let bestIndex;
  do {
    bestValue = 0;
    bestIndex = 0;
    for(let i = 1; i <= game.zones; i++) {
      if( game.zoneOwner[i] === 0 && value[i] >= bestValue ) {
        bestValue = value[i];
        bestIndex = i;
      }
    }
    if( bestIndex > 0 ) {
      game.zoneOwner[bestIndex] = p;
      game.zoneOwnerPrev[bestIndex] = p;
      p++;
      if( p > game.players ) {
        p = 1;
      }
    }
  } while( bestIndex > 0 );
}

export function do123321321AutoPick (game: Game, startingForces: number[][]) {
  let reverse = false;
  const value = calcZoneValues(game, startingForces);
  let p = 1;
  let bestValue;
  let bestIndex;
  do {
    bestValue = 0;
    bestIndex = 0;
    for(let i = 1; i <= game.zones; i++) {
      if( game.zoneOwner[i] === 0 && value[i] >= bestValue ) {
        bestValue = value[i];
        bestIndex = i;
      }
    }
    if( bestIndex > 0 ) {
      game.zoneOwner[bestIndex] = p;
      game.zoneOwnerPrev[bestIndex] = p;
      if( reverse ) {
        p--;
        if( p < 1 ) {
          p = game.players;
        }
      }
      else {
        p++;
        if( p > game.players ) {
          reverse = true;
          p = game.players;
        }
      }
    }
  } while( bestIndex > 0 );
}
